import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Heart, GitCompare } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { proxyImg } from '@/lib/imageProxy';
import { useMall } from '@/lib/mall';
import { isWished, toggleWish, inCompare, toggleCompare, subscribe, WISH_KEY_NAME, CMP_KEY_NAME } from '@/lib/wishlist';

export default function Shop() {
    const [params, setParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(false);
    const { selectedMall, allMalls } = useMall();
    const [filter, setFilter] = useState({
        mall: params.get('mall') || selectedMall?.slug || '',
        category: params.get('category') || '',
        brand: params.get('brand') || '',
        q: params.get('q') || '',
        sort: 'newest',
        page: 1,
    });

    useEffect(() => {
        // Load scraped categories + brands (from inorbitonline.com mirror)
        api.get('/catalog/categories' + (filter.mall ? `?mall_slug=${filter.mall}` : ''))
            .then(({ data }) => setCategories(data));
        api.get('/catalog/brands?limit=150').then(({ data }) => setBrands(data));
    }, [filter.mall]);

    useEffect(() => {
        setLoading(true);
        const qs = new URLSearchParams();
        if (filter.mall) qs.set('mall_slug', filter.mall);
        if (filter.category) qs.set('category', filter.category);
        if (filter.brand) qs.set('brand', filter.brand);
        if (filter.q) qs.set('q', filter.q);
        if (filter.sort) qs.set('sort', filter.sort);
        qs.set('page', filter.page);
        qs.set('page_size', '48');
        api.get(`/catalog/products?${qs.toString()}`).then(({ data }) => {
            setProducts(data.items);
            setTotal(data.total);
        }).finally(() => setLoading(false));
        // update URL
        const urlQs = new URLSearchParams();
        ['mall', 'category', 'brand', 'q'].forEach((k) => filter[k] && urlQs.set(k, filter[k]));
        setParams(urlQs, { replace: true });
    }, [filter, setParams]);

    const set = (k, v) => setFilter({ ...filter, [k]: v, page: k === 'page' ? v : 1 });

    return (
        <div data-testid="shop-page" className="pt-16">
            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-8">
                <div className="overline mb-4">Shop</div>
                <h1 className="font-serif text-5xl md:text-7xl text-inorbit-blue leading-[1.02] tracking-tight max-w-4xl optical-shift">
                    Every brand, <span className="script-tag text-inorbit-orange script-overlap text-6xl md:text-8xl">every mall.</span>
                </h1>
                <p className="mt-6 text-text-secondary text-lg max-w-2xl">
                    {selectedMall ? `Live catalog from Inorbit ${selectedMall.short_name} — ` : 'Live catalog across all Inorbit malls — '}
                    <span className="text-inorbit-blue font-semibold">{total.toLocaleString('en-IN')} products</span> across {brands.length}+ real brands.
                </p>
            </section>

            <section className="border-y border-border-subtle bg-cream sticky top-20 z-20" data-testid="shop-filter-bar">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 bg-white border border-border-subtle px-4 py-2 flex-1 min-w-[200px] rounded">
                        <Search size={16} className="text-text-tertiary" />
                        <input value={filter.q} onChange={(e) => set('q', e.target.value)} placeholder="Search products & brands…" className="flex-1 outline-none text-sm bg-transparent" data-testid="shop-search" />
                    </div>
                    <select className="input-box max-w-[180px]" value={filter.mall} onChange={(e) => set('mall', e.target.value)} data-testid="shop-mall-filter">
                        <option value="">All Malls</option>
                        {allMalls.map((m) => <option key={m.slug} value={m.slug}>{m.short_name}</option>)}
                    </select>
                    <select className="input-box max-w-[200px]" value={filter.category} onChange={(e) => set('category', e.target.value)} data-testid="shop-category-filter">
                        <option value="">All Categories</option>
                        {(() => {
                            const seen = new Set();
                            return categories.filter((c) => seen.has(c.url_key) ? false : (seen.add(c.url_key), true))
                                .map((c) => <option key={c.url_key} value={c.url_key}>{c.name}</option>);
                        })()}
                    </select>
                    <select className="input-box max-w-[180px]" value={filter.brand} onChange={(e) => set('brand', e.target.value)} data-testid="shop-brand-filter">
                        <option value="">All Brands</option>
                        {brands.slice(0, 80).map((b) => <option key={b.slug} value={b.name}>{`${b.name} (${b.product_count})`}</option>)}
                    </select>
                    <select className="input-box max-w-[160px]" value={filter.sort} onChange={(e) => set('sort', e.target.value)} data-testid="shop-sort">
                        <option value="newest">Newest</option>
                        <option value="price_low">Price: low to high</option>
                        <option value="price_high">Price: high to low</option>
                        <option value="discount">Biggest discount</option>
                    </select>
                    <div className="text-xs uppercase tracking-widest text-text-tertiary ml-auto" data-testid="shop-result-count">
                        {loading ? 'Loading…' : `${total.toLocaleString('en-IN')} items`}
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 stagger dim-siblings" data-testid="shop-grid">
                    {products.map((p) => (
                        <ProductCard key={p.id} p={p} />
                    ))}
                    {products.length === 0 && !loading && (
                        <div className="col-span-full text-center text-text-tertiary py-16">No products match your filters.</div>
                    )}
                </div>

                {/* Pagination */}
                {total > 48 && (
                    <div className="flex items-center justify-center gap-3 mt-16" data-testid="shop-pagination">
                        <button onClick={() => set('page', Math.max(1, filter.page - 1))} disabled={filter.page === 1} className="btn-outline disabled:opacity-40" data-testid="shop-prev">Prev</button>
                        <span className="text-sm text-text-secondary">Page {filter.page} of {Math.ceil(total / 48)}</span>
                        <button onClick={() => set('page', filter.page + 1)} disabled={filter.page >= Math.ceil(total / 48)} className="btn-outline disabled:opacity-40" data-testid="shop-next">Next</button>
                    </div>
                )}
            </section>
        </div>
    );
}


function ProductCard({ p }) {
    const [wished, setWished] = useState(isWished(p.id));
    const [compared, setCompared] = useState(inCompare(p.id));
    useEffect(() => subscribe(WISH_KEY_NAME, () => setWished(isWished(p.id))), [p.id]);
    useEffect(() => subscribe(CMP_KEY_NAME, () => setCompared(inCompare(p.id))), [p.id]);

    const onWish = (e) => {
        e.preventDefault(); e.stopPropagation();
        const now = toggleWish(p.id);
        toast.success(now ? 'Saved to wishlist' : 'Removed from wishlist');
    };
    const onCompare = (e) => {
        e.preventDefault(); e.stopPropagation();
        const r = toggleCompare(p.id);
        if (!r.ok) toast.error('You can compare up to 4 items');
        else toast.success(r.present ? 'Added to compare' : 'Removed from compare');
    };

    return (
        <Link to={`/product/${p.id}`} className="card-luxury block img-zoom group relative" data-testid={`product-card-${p.id}`}>
            <div className="aspect-[4/5] overflow-hidden bg-cream relative">
                <img src={proxyImg(p.image || p.images?.[0])} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                {p.discount_pct >= 30 && (
                    <div className="absolute top-3 left-3 bg-inorbit-orange text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded">
                        {p.discount_pct}% OFF
                    </div>
                )}
                {/* Wish + Compare overlay */}
                <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={onWish} className={`w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-md ${wished ? 'bg-rose-500 text-white' : 'bg-white/90 text-inorbit-blue hover:bg-rose-500 hover:text-white'} transition-colors`} aria-label="Wishlist" data-testid={`wish-${p.id}`}>
                        <Heart size={14} fill={wished ? 'currentColor' : 'none'} />
                    </button>
                    <button onClick={onCompare} className={`w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-md ${compared ? 'bg-inorbit-orange text-white' : 'bg-white/90 text-inorbit-blue hover:bg-inorbit-orange hover:text-white'} transition-colors`} aria-label="Compare" data-testid={`cmp-${p.id}`}>
                        <GitCompare size={14} />
                    </button>
                </div>
            </div>
            <div className="p-5">
                <div className="overline text-inorbit-orange text-[10px] mb-1 truncate">{p.brand || 'Inorbit'}</div>
                <div className="text-sm text-inorbit-blue font-medium leading-snug line-clamp-2 mb-3 min-h-[2.5rem]">{p.name}</div>
                <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-semibold text-inorbit-blue">₹{Number(p.price).toLocaleString('en-IN')}</span>
                    {p.mrp > p.price && (
                        <>
                            <span className="text-xs text-text-tertiary line-through">₹{Number(p.mrp).toLocaleString('en-IN')}</span>
                            <span className="text-xs text-inorbit-orange font-semibold">{p.discount_pct}% off</span>
                        </>
                    )}
                </div>
            </div>
        </Link>
    );
}
